// src/pages/payments/TrialActive.jsx
// F5 — Pantalla de éxito para suscripción CON trial (5 días gratis).
// Destino del flujo de suscripción-trial desde PaymentReturn (?activated=true
// + subscription_status === 'TRIAL'). El pago directo (one-shot) usa
// PaymentSuccess; este es el equivalente para el trial.
//
// Datos desde userData (snapshot de /users/{uid}). El primer cobro se muestra
// como hoy+5 (el trial estándar es 5 días; legacy tiene trial 0 y no llega acá).
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../auth/AuthContext';

const PLAN_LABELS = {
  monthly: 'Mensual',
  monthly_legacy: 'Mensual (beta)',
  quarterly: 'Trimestral',
  biannual: 'Semestral',
};

const PLAN_AMOUNTS = {
  monthly: 19990,
  monthly_legacy: 9990,
  quarterly: 47970,
  biannual: 83940,
};

const formatAmount = (amount) =>
  new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(amount);

const fmtDate = (ms) =>
  new Date(ms).toLocaleDateString('es-CL', { day: 'numeric', month: 'long', timeZone: 'America/Santiago' });

// Timestamp Firestore (o ISO) → ms. Soporta {seconds} y .toDate().
const tsToMs = (ts) => {
  if (!ts) return null;
  if (typeof ts.seconds === 'number') return ts.seconds * 1000;
  if (ts.toDate) return ts.toDate().getTime();
  const t = new Date(ts).getTime();
  return Number.isFinite(t) ? t : null;
};

export default function TrialActive() {
  const navigate = useNavigate();
  const { userData } = useAuth();

  const planId = userData?.trial_plan_id || userData?.lastPaymentPlan || 'monthly';
  const planLabel = PLAN_LABELS[planId] || 'Mensual';
  const firstChargeAmount = formatAmount(PLAN_AMOUNTS[planId] || 19990);
  // Fecha REAL del primer cobro desde el doc (next_invoice_date de Flow), no un
  // cálculo cliente hoy+5 que no coincidía con Mi cuenta ni con Flow.
  const nextChargeMs = tsToMs(userData?.nextChargeAt);
  const firstChargeDate = nextChargeMs ? fmtDate(nextChargeMs) : '—';

  return (
    <div className="mobile-root">
      <Helmet><title>¡Trial activo! · Edge Elite</title></Helmet>

      <section
        className="screen"
        style={{ paddingTop: 60, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', paddingBottom: 120 }}
      >
        {/* Icono regalo dorado */}
        <div
          style={{ width: 96, height: 96, borderRadius: 999, background: 'rgba(245,166,35,.14)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 28, fontSize: 44 }}
        >
          🎁
        </div>

        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700, letterSpacing: '-0.025em', margin: 0, marginBottom: 8 }}>
          ¡Tienes 5 días gratis! 🎉
        </h2>
        <p style={{ fontSize: 15, color: 'var(--muted)', margin: 0, marginBottom: 32, maxWidth: 320, lineHeight: 1.5 }}>
          Tu prueba arrancó. Ya tienes el acceso completo.
        </p>

        {/* Resumen verde */}
        <div
          style={{ width: '100%', maxWidth: 320, background: '#F3FDF6', border: '1px solid #BBF7D0', borderRadius: 14, padding: 16, marginBottom: 24, textAlign: 'left' }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #BBF7D0' }}>
            <span style={{ fontSize: 13, color: '#16A34A' }}>Plan</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#14532D' }}>{planLabel}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #BBF7D0' }}>
            <span style={{ fontSize: 13, color: '#16A34A' }}>Hoy pagas</span>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 800, color: '#16A34A', letterSpacing: '-0.03em' }}>$0</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #BBF7D0' }}>
            <span style={{ fontSize: 13, color: '#16A34A' }}>Prueba gratis</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#14532D' }}>5 días</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
            <span style={{ fontSize: 13, color: '#16A34A' }}>Primer cobro</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#14532D' }}>{firstChargeDate} · {firstChargeAmount}</span>
          </div>
        </div>

        {/* Tu acceso incluye — 3 pilares */}
        <div style={{ width: '100%', maxWidth: 320, textAlign: 'left' }}>
          <div className="eyebrow" style={{ marginBottom: 14 }}>Tu acceso incluye</div>
          {[
            { icon: '📊', t: 'Dashboard de análisis en vivo', d: 'Proyección vs línea, edge y veredicto en tiempo real.' },
            { icon: '🔔', t: 'Alertas en vivo por Telegram', d: 'El análisis llega a tu chat cuando el motor detecta valor.' },
            { icon: '👥', t: 'Comunidad VIP', d: 'Comenta las apuestas y aprende con el resto del grupo.' },
          ].map((it, i, arr) => (
            <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: i < arr.length - 1 ? 14 : 0 }}>
              <div style={{ fontSize: 20, flexShrink: 0, lineHeight: 1 }}>{it.icon}</div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{it.t}</div>
                <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 2, lineHeight: 1.45 }}>{it.d}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="sticky-cta">
        <button className="btn-primary accent" onClick={() => navigate('/conectar-telegram')}>
          Conectar Telegram ahora
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
            <path d="M5 12h14M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}
